import { FormEvent, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import {
  Banner,
  Button,
  Flex,
  FormCheckbox,
  FormGroup,
  FormHelpText,
  Link,
} from '@spotify-internal/encore-web';
import {
  AuthScreen,
  FormFieldsBox,
  FormTextField,
  StringUtils,
} from '@spotify/confidence-core-react';
import { useHubspotForm } from '@spotify/confidence-vendor-react';

import { IntroStep } from './IntroStep';

import styles from './UnavailabilityPage.module.css';

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- child component rendered inside tracked UnsupportedMarketRoute
export const UnavailabilityPage = ({ onClose }: { onClose: () => void }) => {
  const [{ loading, error, value }, postData] = useHubspotForm({
    formId: 'e3cb3f9a-d0cd-4460-836c-a72e1e9435f3',
    pageName: 'Confidence App',
  });
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const country = searchParams.get('country') ?? 'unknown';

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    postData(
      [
        { objectTypeId: '0-1', name: 'email', value: email },
        { name: 'company', value: company },
        { name: 'country', value: country },
      ],
      {
        consentToProcess: true,
        text: 'I agree to allow Spotify AB to store and process my personal data.',
        communications: [
          {
            value: true,
            subscriptionTypeId: 999,
            text: 'I agree to receive notifications when Confidence launches in my country.',
          },
        ],
      },
    );
  };

  const disabled =
    company === '' ||
    !StringUtils.isValidEmail(email) ||
    loading ||
    !acceptTerms;

  if (value) {
    return (
      <IntroStep title="Thanks for your interest in Confidence. We'll reach out once we launch in your country." />
    );
  }

  return (
    <AuthScreen.Root>
      <AuthScreen.Main>
        <div className={styles.content}>
          <Flex direction="column" gap="looser-3" alignItems="center">
            <RouterLink to="/" aria-label="Go to homepage" data-track-source-anchor="e94b392716">
              <AuthScreen.Logo />
            </RouterLink>
            <Flex direction="column" gap="tighter-3" alignItems="center">
              <AuthScreen.Title>Get notified</AuthScreen.Title>
              <AuthScreen.Description>
                We&apos;ll let you know when we&apos;re available in your
                country.
              </AuthScreen.Description>
            </Flex>
          </Flex>
          {error && (
            <Banner colorSet="negative" contextual role="alert">
              {error.message}
            </Banner>
          )}
          <form onSubmit={submit} data-track-source-anchor="5849f2d2aa;unavailability-page-form">
            <FormFieldsBox>
              <FormTextField
                label="Email"
                name="email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                size="small"
                value={email}
                onChange={event => setEmail(event.currentTarget.value)}
               data-track-source-anchor="731f72b095"/>
              <FormTextField
                label="Company"
                name="company"
                size="small"
                value={company}
                onChange={event => setCompany(event.currentTarget.value)}
               data-track-source-anchor="a745bfd7b2"/>
              <FormGroup label="Communications consent" hideLabel>
                <FormCheckbox
                  id="terms-of-service"
                  name="terms-of-service"
                  checked={acceptTerms}
                  onChange={() => setAcceptTerms(current => !current)}
                 data-track-source-anchor="4d38efd62c">
                  I agree to receive notifications when Confidence launches in
                  my country.
                </FormCheckbox>
                <FormHelpText>
                  Unsubscribe at any time. Check out our{' '}
                  <Link
                    target="_blank"
                    href="https://www.spotify.com/us/legal/privacy-policy/"
                   data-track-source-anchor="307028ee7a">
                    Privacy Policy
                  </Link>
                  .
                </FormHelpText>
              </FormGroup>
            </FormFieldsBox>
            <Flex
              className={styles.actions}
              gap="tighter-2"
              justifyContent="end"
            >
              <Button
                type="button"
                variant="secondary"
                size="medium"
                onClick={onClose}
               data-track-source-anchor="dda3e4a0f4">
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="medium"
                disabled={disabled}
                isLoading={loading}
                loadingAriaLabel="Submitting notification request"
               data-track-source-anchor="1f6acabd25;;high">
                Submit
              </Button>
            </Flex>
          </form>
        </div>
      </AuthScreen.Main>
    </AuthScreen.Root>
  );
};
